/**
 * Skills Hooks
 *
 * Provides hooks for skill CRUD operations with TanStack Query.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import { uniqueById } from '@/utils/skillOptions';
import type {
  CanonicalLevel,
  CertificateStatus,
  SkillCategoryKey,
} from '@/utils/skillLevels';

// Skill types
export type SkillType = 'service_category' | 'brand_certification' | 'equipment';
/** Dual vocabulary: canonical + legacy wire values. */
export type ProficiencyLevel =
  | CanonicalLevel
  | 'interest'
  | 'beginner'
  | 'intermediate'
  | 'expert';

export interface SkillCertificate {
  status: CertificateStatus;
  issuer?: string | null;
  number?: string | null;
  certifiedAt?: string | null;
  expiresAt?: string | null;
}

export interface Skill {
  id: string;
  name: string;
  type: SkillType;
  code?: string | null;
  category?: SkillCategoryKey | string | null;
  requiresValidCertificate?: boolean;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface MechanicSkill {
  id: string;
  userId: string;
  skillId: string;
  skill: Skill | null;
  proficiencyLevel?: string;
  level?: CanonicalLevel | null;
  wantsToLearn: boolean;
  certificate?: SkillCertificate | { status: CertificateStatus };
  certifiedAt?: string | null;
  expiresAt?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    nextCursor: string | null;
    prevCursor: string | null;
    hasMore: boolean;
  };
}

export interface SkillFilters {
  [key: string]: string | number | boolean | undefined;
  q?: string;
  type?: SkillType;
  category?: SkillCategoryKey;
  isActive?: boolean;
  sortBy?: 'name' | 'sortOrder';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
}

interface CreateSkillData {
  name: string;
  type: SkillType;
  category?: SkillCategoryKey;
  description?: string;
  isActive?: boolean;
  sortOrder?: number;
}

interface UpdateSkillData {
  name?: string;
  type?: SkillType;
  category?: SkillCategoryKey;
  description?: string | null;
  isActive?: boolean;
  sortOrder?: number;
}

interface AssignMechanicSkillData {
  skillId: string;
  proficiencyLevel?: ProficiencyLevel;
  level?: CanonicalLevel | null;
  wantsToLearn?: boolean;
  certifiedAt?: string | null;
  expiresAt?: string | null;
  notes?: string | null;
}

interface UpdateMechanicSkillData {
  proficiencyLevel?: ProficiencyLevel;
  level?: CanonicalLevel | null;
  wantsToLearn?: boolean;
  certifiedAt?: string | null;
  expiresAt?: string | null;
  notes?: string | null;
  certificateIssuer?: string | null;
  certificateNumber?: string | null;
}

/**
 * Hook to fetch paginated list of skills with infinite scroll
 */
export function useSkills(filters: SkillFilters = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.skills.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<PaginatedResponse<Skill>>('/skills', {
        params: {
          ...filters,
          cursor: pageParam,
        },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.nextCursor : undefined,
    staleTime: 60 * 1000, // 1 minute
  });
}

const SKILLS_PAGE_SIZE = 100;
const SKILLS_CATALOG_CAP = 5000;

/**
 * Walk every page of the skills catalog. The list route caps `limit` at 100
 * and the taxonomy is already larger than that (~102 plus tenant extras).
 */
export async function fetchAllActiveSkills(type?: SkillType): Promise<Skill[]> {
  const all: Skill[] = [];
  const seen = new Set<string>();
  let cursor: string | undefined;
  for (;;) {
    const params: Record<string, string | number | boolean> = {
      isActive: true,
      limit: SKILLS_PAGE_SIZE,
      sortBy: 'name',
      sortOrder: 'asc',
    };
    if (type) params['type'] = type;
    if (cursor) params['cursor'] = cursor;
    const page = await api.get<PaginatedResponse<Skill>>('/skills', { params });
    let added = 0;
    for (const skill of page.data) {
      if (seen.has(skill.id)) continue;
      seen.add(skill.id);
      all.push(skill);
      added += 1;
    }
    const nextCursor = page.pagination.hasMore ? page.pagination.nextCursor : null;
    if (!nextCursor || added === 0 || all.length >= SKILLS_CATALOG_CAP) break;
    cursor = nextCursor;
  }
  return uniqueById(all);
}

/**
 * Hook to fetch all active skills (every page, for dropdowns).
 */
export function useAllSkills(type?: SkillType) {
  return useQuery({
    queryKey: queryKeys.skills.list({ type, isActive: true, all: true }),
    queryFn: () => fetchAllActiveSkills(type),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch a single skill by ID
 */
export function useSkill(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.skills.detail(id ?? ''),
    queryFn: () => api.get<Skill>(`/skills/${id}`),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to create a new skill
 */
export function useCreateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSkillData) => api.post<Skill>('/skills', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.skills.lists(),
      });
    },
  });
}

/**
 * Hook to update an existing skill
 */
export function useUpdateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSkillData }) =>
      api.patch<Skill>(`/skills/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.skills.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.skills.lists(),
      });
    },
  });
}

/**
 * Hook to delete (soft delete) a skill
 */
export function useDeleteSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/skills/${id}`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.skills.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.skills.lists(),
      });
    },
  });
}

// ============ Mechanic Skills ============

/**
 * Hook to fetch a mechanic's skills
 */
export function useMechanicSkills(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.mechanicSkills.byUser(userId ?? ''),
    queryFn: () =>
      api.get<{ data: MechanicSkill[] }>(`/users/${userId}/skills`),
    enabled: !!userId,
    staleTime: 60 * 1000,
    select: (data) => data.data,
  });
}

/**
 * The caller's own skills. Supervisors receive levels; everyone else gets
 * the redacted self view (no level / proficiency fields).
 */
export function useMySkills() {
  return useQuery({
    queryKey: [...queryKeys.mechanicSkills.all, 'me'] as const,
    queryFn: () => api.get<{ data: MechanicSkill[] }>('/users/me/skills'),
    staleTime: 60 * 1000,
    select: (data) => data.data,
  });
}

/**
 * Hook to assign a skill to a mechanic
 */
export function useAssignMechanicSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string;
      data: AssignMechanicSkillData;
    }) => api.post<MechanicSkill>(`/users/${userId}/skills`, data),
    onSuccess: (_, { userId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.mechanicSkills.byUser(userId),
      });
    },
  });
}

/**
 * Hook to update a mechanic's skill
 */
export function useUpdateMechanicSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      skillId,
      data,
    }: {
      userId: string;
      skillId: string;
      data: UpdateMechanicSkillData;
    }) => api.patch<MechanicSkill>(`/users/${userId}/skills/${skillId}`, data),
    onSuccess: (_, { userId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.mechanicSkills.byUser(userId),
      });
    },
  });
}

/**
 * Hook to remove a skill from a mechanic
 */
export function useRemoveMechanicSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, skillId }: { userId: string; skillId: string }) =>
      api.delete(`/users/${userId}/skills/${skillId}`),
    onSuccess: (_, { userId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.mechanicSkills.byUser(userId),
      });
    },
  });
}
